/**
 * The goal API (Goals UX pass 2026-09-06). Every read returns the same flat
 * shape the backend's `goal_row` writes; the list asks the board once for
 * every goal's movement, which derives from the tasks, habits and skills under
 * it — nothing is scheduled or logged on a goal itself.
 */
import type { SkillBoardCard, SkillHabitItem, SkillTaskItem } from './skillService'

export type Goal = {
  id: number
  title: string
  description?: string | null
  /** The lifecycle lane: active · in_progress · commitment · parked · paused_by_user · hold · completed · cancelled. */
  status: string
  progress: number
  priority: 'low' | 'medium' | 'high' | 'critical'
  parent_dream_id?: number | null
  parent_aspiration_id?: number | null
  parent?: { type: 'dream' | 'aspiration' | 'goal' | 'skill'; id: number; title: string | null; life_area?: string | null } | null
  target_date?: string | null
  success_criteria?: string | null
  category: string
  goal_type?: string
  complexity_level?: string
  life_area?: string | null
  life_areas?: string[] | null
  is_orphan?: boolean
  completed_at?: string | null
  paused_at?: string | null
  let_go_at?: string | null
  created_at?: string
  updated_at?: string | null
  lane?: string
}

export type CreateGoalRequest = {
  title: string
  description?: string
  success_criteria?: string
  priority?: 'low' | 'medium' | 'high' | 'critical'
  parent_dream_id?: number
  parent_aspiration_id?: number
  target_date?: string
  category?: string
  life_area?: string
  life_areas?: string[]
  status?: string
  progress?: number
}

export type UpdateGoalRequest = {
  title?: string
  description?: string | null
  success_criteria?: string | null
  status?: string
  progress?: number
  priority?: 'low' | 'medium' | 'high' | 'critical'
  parent_dream_id?: number | null
  parent_aspiration_id?: number | null
  target_date?: string | null
  category?: string
  life_area?: string | null
  life_areas?: string[]
}

/** A skill under a goal, with its own board card. */
export interface GoalSkillItem {
  id: number
  title: string
  status?: string | null
  skill_status?: string | null
  current_level?: string | null
  target_level?: string | null
  target_date?: string | null
  practice_cadence?: string | null
  priority?: string | null
  card: SkillBoardCard | null
}

/** A goal's card on the list page (GET /api/hierarchy/goals/board). */
export interface GoalBoardCard {
  path: {
    plan_id: number; status: string; version: number; done: number; total: number; ongoing: number; skills: number; blocked: number
    current: { step_id: string; kind: 'task'; title: string | null; id: number | null } | null
    entity_ids: { task: number[]; habit: number[]; skill: number[] }
  } | null
  /** The next thing to do: the journey's current task, the earliest open task, the soonest habit, or a skill's next. */
  next: { kind: 'task' | 'habit' | 'skill'; id: number; title: string | null; date: string | null; time: string | null; at: string | null; via?: { kind: string | null; id: number | null; title: string | null } } | null
  today: { kind: 'task' | 'habit' | 'skill'; id: number; title: string | null; at: string | null } | null
  last_14: Array<{ date: string; outcome: 'done' | null }>
  days_active: number
  window_days: number
  window_capped: boolean
  days_total: number
  last_at: string | null
  last_date: string | null
  progress: number | null
  open_tasks: number
  done_tasks: number
  items: { tasks: Array<SkillTaskItem & { readiness_status?: string | null; parent_skill_id?: number | null }>; habits: SkillHabitItem[]; skills: GoalSkillItem[] }
  review: { id: string; completed_task_title: string | null; remaining_tasks_count: number | null; all_tasks_done: boolean | null } | null
  words: string
}

export interface GoalBoard { today: string; goals: Record<string, GoalBoardCard> }

export interface GoalJourneyStep {
  step_id: string
  step_ref?: string | null
  order: number
  title: string
  description: string
  estimated_duration_minutes: number
  /** `materialized` is what the server writes once a step's entity exists. */
  status: 'locked' | 'unlocked' | 'materialized' | 'completed' | 'materialization_failed'
  readiness_status?: 'ready' | 'blocked' | string | null
  readiness_reason?: string | null
  child_type?: 'task' | 'skill' | 'habit' | 'roadblock' | string
  entity_type?: string | null
  entity_id?: number | null
  unlocked_at?: string | null
  completed_at?: string | null
  /**
   * The two dependency mechanisms, in step_id space: finite work that must be
   * done, and a rung a skill step must have reached. What lets a waiting step
   * say what it is waiting on instead of only that it waits.
   */
  prerequisite_step_ids?: string[]
  skill_requirement_ids?: Array<{ skill_step_id: string; skill_step_ref?: string | null; required_level?: string | null; required_capability?: string | null }>
  skill_plan?: Record<string, unknown> | null
}

export interface GoalJourneyPlan {
  journey_plan_id: number
  goal_id: number
  journey_schema_version?: number
  status: 'active' | 'completed' | 'archived'
  current_step_index: number
  active_entity_id?: number | null
  active_entity_type?: string | null
  steps: GoalJourneyStep[]
  created_at?: string
  updated_at?: string
}

/** A step as sent to accept-breakdown / the journey edit. */
export interface GoalBreakdownStep {
  step_id?: string
  step_ref?: string | null
  title: string
  description: string
  estimated_duration_minutes: number
  order: number
  child_type?: 'task' | 'skill' | 'habit' | 'roadblock'
  habit?: { rrule?: string; scheduled_times_local?: string[]; preferred_days?: string[]; target_cadence?: string; schedule_start_date?: string; duration_minutes?: number } | null
  task?: { target_window?: string; due_date?: string } | null
  habit_plan?: { cadence?: string | null; days?: string[] | null; time_of_day?: string | null; minutes?: number | null } | null
  skill_plan?: Record<string, unknown> | null
  prerequisite_step_refs?: string[] | null
  skill_requirements?: Array<{ skill_step_ref: string; required_level?: string | null; required_capability?: string | null }> | null
}

async function readJson(r: Response, fallback: string) {
  const body = await r.json().catch(() => ({}))
  if (!r.ok || body.success === false) throw new Error(body.error || fallback)
  return body
}

export class GoalService {
  private baseUrl = '/api/hierarchy/goals'

  async getGoals(includeAll = true): Promise<Goal[]> {
    // include=all: rationing parks fresh goals; an active-only read once
    // rendered this page empty while the dreams page showed the same goals.
    const response = await fetch(`${this.baseUrl}${includeAll ? '?include=all' : ''}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, redirect: 'manual' })
    if (response.status === 302 || response.status === 301 || response.type === 'opaqueredirect' || response.status === 401 || response.status === 403) {
      throw new Error('Authentication required. Please log in to access goals.')
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `Failed to fetch goals: ${response.status}` }))
      throw new Error(error.error || `Failed to fetch goals: ${response.status}`)
    }
    const data = await response.json()
    return Array.isArray(data) ? data : data.data || []
  }

  async getGoal(id: number | string): Promise<Goal> {
    const response = await fetch(`${this.baseUrl}/${id}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `Failed to fetch goal with id ${id}` }))
      throw new Error(error.error || `Failed to fetch goal with id ${id}`)
    }
    const result = await response.json()
    return (result.item || result.data?.item || result.data || result) as Goal
  }

  async getBoard(today: string): Promise<GoalBoard> {
    const r = await fetch(`${this.baseUrl}/board?today=${encodeURIComponent(today)}`, { credentials: 'include' })
    const body = await readJson(r, 'Could not load the goals board')
    return body.data as GoalBoard
  }

  async createGoal(goal: CreateGoalRequest): Promise<Goal> {
    const response = await fetch(this.baseUrl, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(goal), redirect: 'manual' })
    if (response.status === 302 || response.status === 301 || response.type === 'opaqueredirect' || response.status === 401 || response.status === 403) {
      throw new Error('Authentication required. Please log in to create goal.')
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create goal' }))
      throw new Error(error.error || 'Failed to create goal')
    }
    const data = await response.json()
    return (data.data && data.data.id ? data.data : data) as Goal
  }

  async updateGoal(id: number | string, updates: UpdateGoalRequest): Promise<Goal> {
    const response = await fetch(`${this.baseUrl}/${id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update goal' }))
      throw new Error(error.error || 'Failed to update goal')
    }
    const data = await response.json()
    return data.data || data
  }

  async deleteGoal(id: number | string): Promise<{ cleanup: Record<string, number> }> {
    const response = await fetch(`${this.baseUrl}/${id}`, { method: 'DELETE', credentials: 'include' })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete goal' }))
      throw new Error(error.error || 'Failed to delete goal')
    }
    const body = await response.json().catch(() => ({}))
    return { cleanup: (body && body.cleanup) || {} }
  }

  async updateGoalProgress(id: number | string, progress: number): Promise<Goal> {
    return this.updateGoal(id, { progress })
  }

  async markGoalComplete(id: number | string): Promise<Goal> {
    return this.updateGoal(id, { status: 'completed', progress: 100 })
  }

  async previewBreakdown(goalId: number | string): Promise<{ steps: GoalBreakdownStep[]; dependency_edges?: unknown[] }> {
    const response = await fetch(`${this.baseUrl}/${goalId}/preview-breakdown`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not plan the journey just now' }))
      throw new Error(err.error || 'Could not plan the journey just now')
    }
    return response.json()
  }

  async acceptBreakdown(goalId: number | string, steps: GoalBreakdownStep[], dependencyEdges?: unknown[]): Promise<{ journey_plan_id: number | null; created_entity: { id: number; title: string } | null; steps: Array<{ step_id: string; title?: string; status: string; child_type?: string; entity_id?: number | null }> }> {
    const response = await fetch(`${this.baseUrl}/${goalId}/accept-breakdown`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ steps, dependency_edges: dependencyEdges && dependencyEdges.length ? dependencyEdges : undefined }) })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not start the journey' }))
      throw new Error(err.error || 'Could not start the journey')
    }
    return response.json()
  }

  async getJourneyPlan(goalId: number | string): Promise<GoalJourneyPlan | null> {
    const response = await fetch(`${this.baseUrl}/${goalId}/journey-plan`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch journey plan')
    return response.json()
  }

  async updateJourneyPlan(goalId: number | string, steps: GoalBreakdownStep[]): Promise<GoalJourneyPlan> {
    const response = await fetch(`${this.baseUrl}/${goalId}/journey-plan`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ steps }) })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not save the journey' }))
      throw new Error(err.error || 'Could not save the journey')
    }
    return response.json()
  }
}
